import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Modal, Text, Fieldset, FormGroup, Radio, Switch, Input, Select, Box, Small } from '@bigcommerce/big-design';

import { warehouseLabelPrint } from '../../redux/actions/warehouseAreas';

const PrintOptionsModal = ({ payload, areas, isOpen, onClose, print, downloadUrl, isPrinting }) => {
  const [labelWidth, setLabelWidth] = useState('4');
  const [labelLength, setLabelLength] = useState('2');
  const [unit, setUnit] = useState('in');
  const [codeType, setCodeType] = useState('qr');
  const [stacking, setStacking] = useState('horizontal');
  const [reversibleAisle, setReversibleAisle] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (downloadUrl && submitted) {
      setTimeout(() => {
        let win = window.open(downloadUrl, '_blank');
        if (win) {
          win.focus();
        }
      }, 0);

      setSubmitted(false);
      onClose();
    }
  }, [downloadUrl]);

  const onCodeTypeChange = (event) => {
    setCodeType(event.target.value);

    if (event.target.value === 'code128') {
      setStacking('vertical');
    }
  }

  const dimensionsValid = () => {
    return parseFloat(labelWidth) > 0 && parseFloat(labelLength) > 0;
  }

  const confirmPrint = () => {
    setSubmitted(true);

    print(payload, {
      areas: areas,
      label_width: parseFloat(labelWidth),
      label_length: parseFloat(labelLength),
      unit: unit,
      code_type: codeType,
      stacking: stacking,
      reversible_aisle: reversibleAisle
    });
  }

  return(
    <Modal
      actions={[
        {
          text: 'Cancel',
          variant: 'subtle',
          onClick: () => onClose(),
        },
        {
          text: 'Print Labels',
          isLoading: isPrinting,
          disabled: !dimensionsValid() || isPrinting,
          onClick: () => confirmPrint()
        },
      ]}
      closeOnClickOutside={true}
      closeOnEscKey={true}
      header="Print Warehouse Design Labels"
      isOpen={isOpen}
      onClose={() => onClose()}
    >
      <Box
        backgroundColor="primary10"
        border="box"
        borderRadius="normal"
        padding="medium"
      >
        <Text>This feature only supports label printing for thermal printer rolls.</Text>
      </Box>

      <Fieldset legend="Label Dimensions">
        <FormGroup>
          <Input
            label="Width"
            type="number"
            min="0"
            step="any"
            value={labelWidth}
            onChange={(event) => setLabelWidth(event.target.value)}
            required
          />
          <Input
            label="Length"
            type="number"
            min="0"
            step="any"
            value={labelLength}
            onChange={(event) => setLabelLength(event.target.value)}
            required
          />
          <Select
            label="Unit"
            required
            onOptionChange={(value) => setUnit(value)}
            options={[
              { value: 'in', content: 'Inches' },
              { value: 'mm', content: 'Millimeters' }
            ]}
            value={unit}
          />
        </FormGroup>
      </Fieldset>

      <Fieldset legend="Code Type">
        <FormGroup>
          <Radio
            checked={codeType === 'qr'}
            label="QR Code"
            onChange={onCodeTypeChange}
            value="qr"
          />
          <Radio
            checked={codeType === 'code128'}
            label="Code 128 Barcode"
            onChange={onCodeTypeChange}
            value="code128"
          />
        </FormGroup>
      </Fieldset>

      <Fieldset legend="Stacking">
        <FormGroup>
          <Radio
            checked={stacking === 'horizontal'}
            disabled={codeType === 'code128'}
            label="Horizontal (code on the left, text on the right)"
            onChange={(event) => setStacking(event.target.value)}
            value="horizontal"
          />
          <Radio
            checked={stacking === 'vertical'}
            label="Vertical (code on top, text below)"
            onChange={(event) => setStacking(event.target.value)}
            value="vertical"
          />
        </FormGroup>
        { codeType === 'code128' &&
          <Small>Horizontal stacking is only available for QR codes.</Small>
        }
      </Fieldset>

      <Fieldset legend="Reversible Aisle">
        <Text>Print two labels per area - one for each sorting direction.</Text>
        <Switch checked={reversibleAisle} onChange={() => setReversibleAisle(!reversibleAisle)} />
      </Fieldset>
    </Modal>
  )
}

const mapStateToProps = state => ({
  downloadUrl: state.warehouseAreas.downloadUrl,
  isPrinting: state.warehouseAreas.isPrinting,
});

const mapDispatchToProps = (dispatch) => ({
  print: (payload, options) => dispatch(warehouseLabelPrint(payload, options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PrintOptionsModal);
